// src/pages/RegionsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import styles from './RegionsPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import apiClient from '../services/ApiClient';
import { toast } from 'react-toastify';

// Anahtar olarak originalDataId'yi kullanacağız.
const transformApiRegionData = (apiDataArray) => {
    if (!apiDataArray || !Array.isArray(apiDataArray)) {
        console.error("transformApiRegionData: Beklenen formatta (dizi) veri gelmedi.", apiDataArray);
        return [];
    }
    return apiDataArray.map((region) => ({
        id: String(region.originalDataId), // Benzersiz ID olarak originalDataId'yi kullanalım
        name: region.name,
        imgUrl: region.imgUrl || '', // Boşsa boş string
        isActive: region.isActive === null || region.isActive === undefined ? true : region.isActive,
        countries: region.countries || [], // Dizi yoksa boş dizi
        countriesTr: region.countriesTr || [],
        countriesDe: region.countriesDe || [],
        // State içinde tutacağımız ve kullanıcı tarafından değiştirilebilecek geçici değerler
        // Eğer bu değerler başlangıçta API'den gelenlerle aynı olacaksa, burada atayabiliriz.
        // Ancak, her satır için ayrı input state'i daha yönetilebilir olabilir.
        // Şimdilik direkt API'den gelenleri kullanıp, değiştikçe tüm region objesini güncelleyeceğiz.
    }));
};


const RegionsPage = () => {
    const [regions, setRegions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingStates, setEditingStates] = useState({}); // Her satır için düzenleme durumlarını tutar { regionId: { imgUrl: '...', isActive: true/false } }
    const [savingStates, setSavingStates] = useState({}); // Hangi satırın kaydedildiğini tutar { regionId: boolean }


    const fetchRegions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/regions');
            let transformedData = [];
            if (response.data && Array.isArray(response.data)) {
                transformedData = transformApiRegionData(response.data);
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                transformedData = transformApiRegionData(response.data.data);
            } else {
                throw new Error("Bölge verileri alınamadı.");
            }
            setRegions(transformedData);
            const initialEditingStates = {};
            transformedData.forEach(region => {
                initialEditingStates[region.id] = {
                    imgUrl: region.imgUrl,
                    isActive: region.isActive
                };
            });
            setEditingStates(initialEditingStates);
        } catch (e) {
            console.error("Bölgeler çekilirken hata:", e);
            setError(e.message || "Bölgeler yüklenirken bir sorun oluştu.");
            setRegions([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRegions();
    }, [fetchRegions]);

    const handleInputChange = (regionId, field, value) => {
        setEditingStates(prev => ({
            ...prev,
            [regionId]: {
                ...prev[regionId],
                [field]: value,
                // Değişiklik yapıldığını işaretlemek için bir flag eklenebilir (opsiyonel)
                // isModified: true
            }
        }));
    };

    const handleToggleActive = (regionId) => {
        setEditingStates(prev => ({
            ...prev,
            [regionId]: {
                ...prev[regionId],
                isActive: !prev[regionId]?.isActive,
                // isModified: true
            }
        }));
    };

    // Satırda kaydedilmemiş değişiklik var mı? Hem "Kaydedilmedi" rozetini
    // hem de Kaydet butonunun aktifliğini bu belirler.
    const isRowDirty = (regionId) => {
        const original = regions.find(r => r.id === regionId);
        const edited = editingStates[regionId];
        if (!original || !edited) return false;
        return original.imgUrl !== edited.imgUrl || original.isActive !== edited.isActive;
    };

    const handleSaveChanges = async (regionId) => {
        const originalRegion = regions.find(r => r.id === regionId); // API'den gelen son kaydedilmiş hali
        const editedStateForRegion = editingStates[regionId]; // Kullanıcının yaptığı son değişiklikler

        if (!originalRegion || !editedStateForRegion) {
            setError("Kaydedilecek bölge bulunamadı veya düzenleme verisi eksik.");
            return;
        }
        setError(null);

        // Sadece değişen alanları içeren bir requestBody oluştur
        const requestBody = {};
        let hasChanges = false;

        // imgUrl değişti mi kontrol et
        if (originalRegion.imgUrl !== editedStateForRegion.imgUrl) {
            requestBody.imgUrl = editedStateForRegion.imgUrl;
            hasChanges = true;
        }

        // isActive değişti mi kontrol et
        if (originalRegion.isActive !== editedStateForRegion.isActive) {
            requestBody.isActive = editedStateForRegion.isActive;
            hasChanges = true;
        }

        // Eğer hiçbir değişiklik yapılmadıysa API'ye istek atmaya gerek yok
        if (!hasChanges) {
            toast.info(`"${originalRegion.name}" bölgesinde değişiklik yapılmadı.`);
            return;
        }

        // API'nız PATCH için diğer zorunlu alanları (örn: name) bekliyorsa,
        // ve bu alanlar düzenlenmiyorsa, bunları originalRegion'dan ekleyebilirsiniz.
        // Ancak ideal PATCH sadece değişenleri gönderir.
        // Örnek: requestBody.name = originalRegion.name; (Eğer API zorunlu tutuyorsa)

        setSavingStates(prev => ({ ...prev, [regionId]: true })); // Bu satır için kaydetme başladı

        try {
            // API endpoint'i için regionId (originalDataId) kullanılıyor.
            await apiClient.patch(`/regions-data/${regionId}`, requestBody);

            // Başarılı olursa, ana `regions` state'ini güncelle
            // ve editingStates'i de senkronize et ki "değişmemiş" durumuna dönsün.
            setRegions(prevRegions =>
                prevRegions.map(r =>
                    r.id === regionId ? { ...r, ...editedStateForRegion } : r
                )
            );
            // editingStates'i de senkronize edelim ki orijinal ve düzenlenmiş haller aynı olsun.
            // Böylece tekrar kaydet'e basıldığında "değişiklik yapılmadı" uyarısı doğru çalışır.
            setEditingStates(prevEditingStates => ({
                ...prevEditingStates,
                [regionId]: { ...editedStateForRegion } // Artık bu API'deki son hali
            }));

            toast.success(`"${originalRegion.name}" bölgesi başarıyla güncellendi.`);
        } catch (err) {
            console.error(`Bölge "${originalRegion.name}" güncellenirken hata:`, err);
            setError(`"${originalRegion.name}" güncellenirken bir hata oluştu: ${err.response?.data?.message || err.message}`);
            // Hata durumunda editingStates'i geri alMAYA gerek yok, kullanıcı hatayı görüp tekrar deneyebilir.
            // Ya da orijinal haline geri döndürebilirsiniz:
            // setEditingStates(prev => ({ ...prev, [regionId]: { imgUrl: originalRegion.imgUrl, isActive: originalRegion.isActive }}));
        } finally {
            setSavingStates(prev => ({ ...prev, [regionId]: false })); // Kaydetme bitti
        }
    };


    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>
                        Bölgeler Yönetimi
                        {regions.length > 0 && <span className={`${styles.count} num`}>{regions.length}</span>}
                    </h1>
                    <p className={styles.subtitle}>
                        Bölge bayraklarını ve yayın durumunu satır bazında düzenleyin.
                        Değişiklikler satırın <strong>Kaydet</strong> butonuna basana kadar kaydedilmez.
                    </p>
                </div>
            </div>

            {error && regions.length > 0 && <p className={styles.errorTextPageTop}>{error}</p>}

            {isLoading && regions.length === 0 && (
                <div className={styles.loadingContainer}>
                    <LoadingSpinner />
                    <p>Bölgeler yükleniyor...</p>
                </div>
            )}

            {error && regions.length === 0 && !isLoading && (
                <div className={styles.errorPanel}>
                    <p className={styles.errorPanelText}>Veriler alınamadı. Bağlantıyı kontrol edip tekrar deneyin.</p>
                    <button type="button" onClick={fetchRegions} className={`${styles.retryButton} ghost`}>
                        Tekrar dene
                    </button>
                </div>
            )}

            {regions.length === 0 && !isLoading && !error && (
                <div className={styles.emptyState}>Gösterilecek bölge bulunamadı.</div>
            )}

            {regions.length > 0 && (
                <div className={styles.tableWrapper}>
                <table className={styles.regionsTable}>
                    <thead>
                        <tr>
                            <th>Bölge Adı</th>
                            <th>Kapsanan Ülkeler (Varsayılan)</th>
                            <th>Bayrak Önizleme / URL</th> {/* Başlık Güncellendi */}
                            <th>Durum</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {regions.map(region => (
                            <tr
                                key={region.id}
                                className={[
                                    !editingStates[region.id]?.isActive ? styles.disabledRow : '',
                                    isRowDirty(region.id) ? styles.dirtyRow : '',
                                ].filter(Boolean).join(' ')}
                            >
                                <td>{region.name}</td>
                                <td className={styles.coveredCountriesCell} title={region.countries.join(', ')}>
                                    {region.countries.slice(0, 3).join(', ')}
                                    {region.countries.length > 3 && `, ... (${region.countries.length - 3} daha)`}
                                    {region.countries.length === 0 && '-'}
                                </td>
                                <td className={styles.flagCell}>
                                    <div className={styles.flagPreviewContainer}>
                                        {editingStates[region.id]?.imgUrl ? (
                                            <img
                                                src={editingStates[region.id]?.imgUrl} // Güncel URL'i göster
                                                alt={`${region.name} Bayrağı`}
                                                className={styles.flagPreviewImage}
                                                onError={(e) => {
                                                    // Eğer URL yüklenemezse, geçici bir placeholder veya hata mesajı gösterilebilir
                                                    e.target.style.display = 'none'; // Resmi gizle
                                                    // Veya e.target.src = 'placeholder_flag_url.png';
                                                    // Ya da bir sonraki span'ı göster
                                                    const nextSibling = e.target.nextElementSibling;
                                                    if (nextSibling && nextSibling.classList.contains(styles.flagPlaceholder)) {
                                                        nextSibling.style.display = 'inline';
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <span className={styles.flagPlaceholder}>🏴</span> // URL yoksa veya yüklenemezse
                                        )}
                                    </div>
                                    <input
                                        type="text"
                                        value={editingStates[region.id]?.imgUrl || ''}
                                        onChange={(e) => handleInputChange(region.id, 'imgUrl', e.target.value)}
                                        className={styles.imageUrlInput}
                                        placeholder="Bayrak URL'i"
                                        disabled={savingStates[region.id]}
                                    />
                                </td>
                                <td className={styles.statusCell}>
                                    <StatusToggle
                                        label={`${region.name} — yayın durumu`}
                                        checked={editingStates[region.id]?.isActive}
                                        saving={savingStates[region.id]}
                                        onChange={() => handleToggleActive(region.id)}
                                    />
                                </td>
                                <td className={styles.actionsCell}>
                                    <button
                                        onClick={() => handleSaveChanges(region.id)}
                                        disabled={savingStates[region.id] || !isRowDirty(region.id)}
                                        className={`${styles.actionButton} ${styles.saveRowButton}`}
                                    >
                                        {savingStates[region.id] ? 'Kaydediliyor…' : 'Kaydet'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}
        </div>
    );
};

export default RegionsPage;